/**
 * StepperRow — molecule (CES standard)
 * One instance-type row with a +/- stepper. Shared by the slotting editor
 * and the sandbox droplet editor. Rows are designed to sit inside a
 * `divide-y` container (see pages).
 * Test IDs: row-{name}, inc-{name}, dec-{name}, count-{name}.
 */

const STEP_BTN_CLASSES =
  'w-8 h-8 rounded-full border border-gray-300 bg-white text-base leading-none text-gray-700 cursor-pointer transition-colors hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-700';

function clampCount(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(9999, Math.trunc(value)));
}

export function StepperRow({
  name,
  specsLabel,
  count,
  disabled = false,
  onChange,
}: {
  name: string;
  specsLabel: string;
  count: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <div
      className="flex items-center justify-between gap-4 bg-white px-5 py-3.5"
      data-testid={`row-${name}`}
    >
      <div>
        <span className="block font-bold text-gray-900">{name}</span>
        <span className="text-xs text-gray-500">{specsLabel}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={STEP_BTN_CLASSES}
          aria-label={`Decrease ${name}`}
          data-testid={`dec-${name}`}
          disabled={disabled}
          onClick={() => onChange(clampCount(count - 1))}
        >
          −
        </button>
        <input
          className="w-16 rounded border border-gray-300 bg-white px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          type="number"
          min={0}
          max={9999}
          value={count}
          data-testid={`count-${name}`}
          aria-label={`${name} count`}
          disabled={disabled}
          onChange={(e) => onChange(clampCount(Number(e.target.value)))}
        />
        <button
          type="button"
          className={STEP_BTN_CLASSES}
          aria-label={`Increase ${name}`}
          data-testid={`inc-${name}`}
          disabled={disabled}
          onClick={() => onChange(clampCount(count + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}
